import { useCallback, useMemo, type ReactElement } from 'react';
import { Formik } from 'formik';
import get from 'lodash/get';
import { cn } from '@fshr/shared-react';
import { isEmailValid } from '@fshr/shared';
import BevSafeText from '../../../bev-safe-text';
import Button from '../../../button';

import buildBookingFormState, { clampOccupancyOnChange } from './build-booking-form-state';
import BookingFormField from './booking-form-field';
import { INPUT_TYPES } from './constants';
import type { NormalizedFieldSet, Price } from './types';

export type BookingFormProps = {
	price: Price;
	isBooked: boolean;
	isSubmitting: boolean;
	t: (text: string, params?: (string | number)[]) => string;
	onSubmit: (formData: unknown) => void;
	className?: string;
	formId?: string;
	hideSubmitButton?: boolean;
};

/**
 * Matches the Figma "Select Preferences" section: a Formik form built from the selected price's
 * fieldSets, classified into quantity steppers, free-standing additional fields, and labelled/grouped
 * fields, with live adult/children occupancy clamping. Inline replacement for the form step that used
 * to live inside `AddToCartFormModal` (via `ProductItem`).
 */
export default function BookingForm({
	price,
	isBooked,
	isSubmitting,
	t,
	onSubmit,
	className,
	formId,
	hideSubmitButton,
}: BookingFormProps): ReactElement {
	const fieldSets = (price.fieldSets ?? []) as unknown as NormalizedFieldSet[];

	const { initialValues, minMaxOcc, quantityFields, additionalFields, labelledFields } = useMemo(
		() => buildBookingFormState(fieldSets, t),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[price.id]
	);

	// Only the fieldSets the form actually renders get validated. Hidden fieldSets (e.g. the
	// ride-along `code` field) are excluded so a required-but-invisible field can never wedge the
	// form into a permanently unsubmittable state the user has no way to clear.
	const validationFields = useMemo(
		() => fieldSets.filter(fieldSet => !fieldSet.hidden).flatMap(fieldSet => fieldSet.fields ?? []),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[price.id]
	);

	// `additionalNames` aliases (e.g. `numAdults`, `quantity.adults`) that mirror a field's value onto
	// other Formik paths the booking payload reads. `buildInitialValues` seeds them once; this map lets
	// `handleFieldChange` keep them in sync on every edit, matching the legacy `FieldSelector`.
	const aliasesByFieldName = useMemo(
		() =>
			new Map(
				fieldSets
					.flatMap(fieldSet => fieldSet.fields ?? [])
					.map(field => [field.name, field.additionalNames ?? []])
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[price.id]
	);

	const validate = useCallback(
		(values: Record<string, unknown>) => {
			const errors: Record<string, string> = {};
			validationFields.forEach(field => {
				const value = get(values, field.name);
				const isBlank = value === undefined || value === null || String(value).trim() === '';

				if (field.type === INPUT_TYPES.EMAIL) {
					// Required emails must be present + valid; optional emails, once filled, must still
					// be valid (the browser enforced this before the form went `noValidate`).
					const invalid = isBlank ? Boolean(field.required) : !isEmailValid(String(value));
					if (invalid) errors[field.name] = t('Please enter a valid email address.');
					return;
				}

				if (!field.required) return;

				if (field.type === INPUT_TYPES.CHECKBOX) {
					// Checkbox values are booleans; `String(false)` isn't blank, so check explicitly.
					if (value !== true) errors[field.name] = t('This field is required.');
					return;
				}

				if (isBlank) errors[field.name] = t('This field is required.');
			});
			return errors;
		},
		[validationFields, t]
	);

	const textareaFields = additionalFields.filter(f => f.type === INPUT_TYPES.TEXTAREA);
	const nonTextareaFields = additionalFields.filter(f => f.type !== INPUT_TYPES.TEXTAREA);

	return (
		<Formik
			initialValues={initialValues}
			enableReinitialize
			validate={validate}
			onSubmit={onSubmit}
		>
			{formikProps => {
				const handleFieldChange = (
					evt:
						| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
						| { target: { name: string; value: unknown } }
				) => {
					const { name, value } = evt.target;
					clampOccupancyOnChange(minMaxOcc, name, Number(value));
					formikProps.setFieldValue(name, value);
					(aliasesByFieldName.get(name) ?? []).forEach(alias =>
						formikProps.setFieldValue(alias, value)
					);
				};

				// Surface a field's validation error only once the user has attempted to submit —
				// applies to every rendered field, not just the labelled/grouped ones.
				const fieldError = (fieldName: string): string | undefined =>
					formikProps.submitCount > 0
						? (formikProps.errors[fieldName] as string | undefined)
						: undefined;

				return (
					<form
						id={formId}
						noValidate
						onSubmit={event => {
							formikProps.handleSubmit(event);
							// `handleSubmit` validates asynchronously, so re-run the (pure, memoised)
							// validator here to synchronously locate the first invalid field to scroll to.
							const firstInvalid = Object.keys(validate(formikProps.values))[0];
							if (firstInvalid) {
								document
									.getElementById(firstInvalid)
									?.scrollIntoView({ behavior: 'smooth', block: 'start' });
							}
						}}
						className={cn('flex flex-col gap-300', className)}
						data-testid="booking-form"
					>
						{nonTextareaFields.length > 0 && (
							<div className="flex flex-col gap-200">
								{nonTextareaFields.map(field => (
									<BookingFormField
										key={field.name}
										field={field}
										value={get(formikProps.values, field.name)}
										errorMessage={fieldError(field.name)}
										onChange={handleFieldChange}
										onBlur={formikProps.handleBlur}
									/>
								))}
							</div>
						)}

						{quantityFields.length > 0 && (
							<div className="flex flex-col gap-200">
								{quantityFields.map(field => (
									<BookingFormField
										key={field.name}
										field={field}
										value={get(formikProps.values, field.name)}
										errorMessage={fieldError(field.name)}
										onChange={handleFieldChange}
										onBlur={formikProps.handleBlur}
									/>
								))}
								{(minMaxOcc.minMessage || minMaxOcc.maxMessage) && (
									<p className="text-caption text-text-secondary">
										{[minMaxOcc.minMessage, minMaxOcc.maxMessage].filter(Boolean).join(' ')}
									</p>
								)}
							</div>
						)}

						{labelledFields.map(labelledField => {
							const hasRequired = labelledField.fields.some(f => f.required);
							return (
								<div key={labelledField.label} className="flex flex-col gap-150">
									<div className="flex flex-col gap-040">
										<div className="flex items-baseline gap-040">
											<span className="text-subtitle2 text-text-heading">
												<BevSafeText>{labelledField.label}</BevSafeText>
											</span>
											{hasRequired && (
												<span className="text-error-text" aria-hidden="true">
													*
												</span>
											)}
										</div>
										{labelledField.secondaryLabel && (
											<span className="text-caption text-text-secondary">
												<BevSafeText>{labelledField.secondaryLabel}</BevSafeText>
											</span>
										)}
									</div>
									<div className="flex flex-col gap-150">
										{labelledField.fields.map(field => (
											<BookingFormField
												key={field.name}
												field={field}
												value={get(formikProps.values, field.name)}
												errorMessage={fieldError(field.name)}
												onChange={handleFieldChange}
												onBlur={formikProps.handleBlur}
											/>
										))}
									</div>
									{/* AC 7.3.3: optional disclaimer for "Base Rate + Per Person"
									    pricing — only appears when the price carries a note (the CMS
									    authors this text for experiences whose base rate covers a set
									    occupancy and charges extra per additional guest). */}
									{labelledField.occupancyValidation && price.note && (
										<p className="text-caption text-text-secondary">
											<BevSafeText>{price.note}</BevSafeText>
										</p>
									)}
								</div>
							);
						})}

						{textareaFields.length > 0 && (
							<div className="flex flex-col gap-200">
								{textareaFields.map(field => (
									<BookingFormField
										key={field.name}
										field={field}
										value={get(formikProps.values, field.name)}
										errorMessage={fieldError(field.name)}
										onChange={handleFieldChange}
										onBlur={formikProps.handleBlur}
									/>
								))}
							</div>
						)}

						{!hideSubmitButton && (
							<Button
								type="submit"
								intent="filled"
								disabled={formikProps.isSubmitting || isSubmitting}
							>
								{isBooked ? t('Continue') : t('Add To Cart')}
							</Button>
						)}
					</form>
				);
			}}
		</Formik>
	);
}
